import { html, LitElement, TemplateResult } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_INTEGRITY_MONITOR_RULE_SETS } from '../../../../../../../webcomponents.constants';
import {
  Column,
  ColumnKeys,
  GRID_COLUMNS,
  RuleSetItem,
} from '../../../../../../integrityMonitor/realTime/integrityMonitorPolicyRuleSetsSection/types';
import { IntegrityMonitorPolicyRuleSetsGridDataProvider } from '../../../../../../integrityMonitor/realTime/integrityMonitorPolicyRuleSetsSection/dataProvider/IntegrityMonitorPolicyRuleSetsGridDataProvider';
import { GridViewWebComponent } from '../../../../../../../grid/gridView';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { styles } from '../view/styles';
import { GzEnumFilterWebComponent } from '../../../../../../../ui/gzEnumFilter';
import { EnumListItem } from '../../../../../../../ui/gzEnumList/types';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { property } from 'lit/decorators.js';
import { GzInfoBoxWebComponent } from '../../../../../../../ui/gzInfoBox';
import { toggleVisibility } from '../../../../../../../utils/htmlElement.utils';
import { IntegrityMonitorRuleSets } from '../utils/helper';
import { InputWidthValues } from '../../../../../../../ui/gzInput';

@customElement(WEB_COMPONENT_POLICY_PANEL_INTEGRITY_MONITOR_RULE_SETS)
export class GzIntegrityMonitorRuleSetsWebComponent extends LitElement {
  /**
   * Additional information used for grid and selector.
   */
  @property({ type: Object, attribute: true, reflect: true })
  public additionalInfo: {
    selectedRuleSets: Array<RuleSetItem>;
    companyRuleSets: Array<RuleSetItem>;
  } | null = null;

  /**
   * Subject to emit the valid state of the component.
   */
  private validSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  /**
   * Observable to get the valid state of the component.
   */
  public valid$: Observable<boolean> = this.validSubject.pipe(distinctUntilChanged());

  /**
   * Used to determine if the component should be validated.
   */
  private shouldBeValidated = false;

  /**
   * Reference to the selected rule sets ids. Must be kept in sync with data.selectedRuleSets if available.
   */
  private selectedRuleSetIdsRef: Array<string>;

  @query('#rule-sets-grid')
  private ruleSetsGrid: GridViewWebComponent<Column, ColumnKeys>;

  @query('#rule-sets-selector')
  private ruleSetsSelector: GzEnumFilterWebComponent;

  @query('#rule-sets-notice')
  private ruleSetsNotice: GzInfoBoxWebComponent;

  /**
   * Styles for the component.
   */
  static get styles() {
    return styles;
  }

  /**
   * Set value of the component (ids of the selected rule sets).
   * @param value
   */
  set value(value: Array<string>) {
    this.selectedRuleSetIdsRef = value;
    this.validate();
  }

  /**
   * Get value of the component (ids of the selected rule sets).
   */
  public get value(): Array<string> {
    return this.selectedRuleSetIdsRef;
  }

  /**
   * Get selected rule sets (rule sets with all the data).
   */
  public get selectedRuleSets(): Array<RuleSetItem> | null {
    if (!this.additionalInfo) {
      return null;
    }

    return this.additionalInfo.selectedRuleSets;
  }

  /**
   * Get component valid state.
   */
  public get valid(): boolean {
    return this.validSubject.value;
  }

  /**
   * Enable/disable validation.
   * @param isValidationDisabled
   */
  public set validationDisabled(isValidationDisabled: boolean) {
    this.shouldBeValidated = !isValidationDisabled;

    if (isValidationDisabled) {
      this.changeValidity(true);
    } else {
      this.validate();
    }
  }

  /**
   * Reset the component to its initial state.
   */
  public reset() {
    this.additionalInfo = null;
    this.selectedRuleSetIdsRef = [];
    this.changeValidity(true);
  }

  /**
   * Render specific content.
   */
  protected render() {
    // no need to render if data is missing
    if (!this.additionalInfo) {
      return html``;
    }

    // sort company rule sets by name
    this.sortCompanyRuleSets();
    // wait for the component to be updated before building the grid and the selector
    this.updateComplete.then(() => {
      this.buildGrid();
      this.buildRuleSetsSelector();
      this.validate();
    });

    return html`
      <div class="rule-sets-form--row">
        <gz-enum-filter
          class="rule-sets-form--field"
          id="rule-sets-selector"
          labelWidth=${InputWidthValues.XXLARGE}
        ></gz-enum-filter>
        <gz-icon-button
          class="rule-sets-form--action"
          id="addButton"
          size="32"
          fontSize="20"
          buttonType="square"
          icon="ds-icon-add"
          buttonFill="primary"
          @click="${this.onRuleSetsSelectionChange}"
        >
        </gz-icon-button>
      </div>

      <gz-info-box class="hidden" severity="warning" id="rule-sets-notice">
        ${translateFn(
          'To enable real-time monitoring, select at least one rule set. Rules are defined in Policies > Integrity Monitoring Rules.'
        )}
      </gz-info-box>

      <gz-grid-view class="grid-view" id="rule-sets-grid"> </gz-grid-view>
    `;
  }

  /**
   * Sort company rule sets by name for displaying them in alphabetical order in the selector.
   */
  private sortCompanyRuleSets(): void {
    this.additionalInfo?.companyRuleSets.sort((ruleSet1: RuleSetItem, ruleSet2: RuleSetItem) => {
      const name1 = ruleSet1.name.toLocaleLowerCase();
      const name2 = ruleSet2.name.toLocaleLowerCase();

      return name1 === name2 ? 0 : name1 > name2 ? 1 : -1;
    });
  }

  /**
   * Build rule sets selector.
   */
  private buildRuleSetsSelector(): void {
    this.ruleSetsSelector.placeholder = translateFn('Add rule sets');
    this.ruleSetsSelector.searchPlaceholderString = translateFn('Search');
    this.ruleSetsSelector.confirmActionButtonString = translateFn('Apply');
    this.ruleSetsSelector.noSearchResultsString = translateFn('No rule sets found.');
    this.ruleSetsSelector.allItemsLabel = translateFn('All rule sets');
    this.computeFilterOptions();
  }

  /**
   * Build grid.
   */
  private buildGrid(): void {
    if (!this.additionalInfo) {
      return;
    }

    this.ruleSetsGrid.propertiesSet = false;
    this.ruleSetsGrid.columns = GRID_COLUMNS;
    this.ruleSetsGrid.viewId = 'integrity-monitor-rule-sets-view-v2';
    this.ruleSetsGrid.allColumnDetails = IntegrityMonitorRuleSets.getGridColumnDetails({
      ruleSet: translateFn('Rule set'),
      rules: translateFn('Rules'),
      actions: translateFn('Action'),
    });
    this.ruleSetsGrid.isCompactViewChecked = true;
    this.ruleSetsGrid.removeFilters = true;
    this.ruleSetsGrid.hideHeaderActions = true;
    this.ruleSetsGrid.hideFooter = true;
    this.ruleSetsGrid.dataProvider = new IntegrityMonitorPolicyRuleSetsGridDataProvider(
      this.additionalInfo.selectedRuleSets
    );
    this.ruleSetsGrid.columnRenderers = {
      [ColumnKeys.ACTIONS]: this.renderActionsCell.bind(this),
    };
    this.ruleSetsGrid.customStrings = {
      noSearchResultsString: translateFn('No rule sets selected.'),
    };
    this.ruleSetsGrid.propertiesSet = true;
    this.toggleGridVisibility();

    if (!this.ruleSetsGrid.started) {
      this.ruleSetsGrid.injectDependencies({
        translateFn: translateFn,
      });
      this.ruleSetsGrid.start().catch(console.error);
    }
  }

  /**
   * Render actions cell (on click, remove rule set from grid).
   * @param rowData
   */
  private renderActionsCell(rowData: { item: RuleSetItem }): TemplateResult {
    return html` <gz-icon-button
      icon="ds-icon-cancel"
      buttoncolor="secondary"
      fontsize="20"
      buttontype="round"
      buttonfill="quiet"
      size="24"
      @click="${() => this.removeRuleSet(rowData.item._id)}"
    ></gz-icon-button>`;
  }

  /**
   * Remove rule set from grid and update filter options.
   * @param itemId
   */
  private removeRuleSet(itemId: string): void {
    if (!this.additionalInfo) {
      return;
    }

    const idx = this.additionalInfo.selectedRuleSets.findIndex(ruleSet => ruleSet._id === itemId);
    this.additionalInfo.selectedRuleSets.splice(idx, 1);

    const idxRef = this.selectedRuleSetIdsRef.findIndex(ruleSetId => ruleSetId === itemId);
    this.selectedRuleSetIdsRef.splice(idxRef, 1);

    this.computeFilterOptions();
    this.computeGridValues();
  }

  /**
   * Add rule sets to the grid and update filter options.
   * @param event
   */
  private onRuleSetsSelectionChange(event: CustomEvent): void {
    if (!this.additionalInfo) {
      return;
    }

    const selectedRuleSetIds = this.ruleSetsSelector.filterOptions
      .filter(option => option.selected)
      .map(option => option.value as string);
    const oldSelectedRuleSetIds = this.additionalInfo.selectedRuleSets.map(ruleSet => ruleSet._id);
    const newRuleSetIds = selectedRuleSetIds.filter(ruleSetId => !oldSelectedRuleSetIds.includes(ruleSetId));
    const newRuleSets = this.additionalInfo.companyRuleSets.filter(ruleSet => newRuleSetIds.includes(ruleSet._id));

    if (!newRuleSets.length) {
      return;
    }

    this.additionalInfo.selectedRuleSets.push(...newRuleSets);
    this.selectedRuleSetIdsRef.push(...newRuleSetIds);

    this.computeFilterOptions();
    this.computeGridValues();
  }

  /**
   * Update filter options.
   */
  private computeFilterOptions() {
    if (!this.additionalInfo) {
      return;
    }

    const selectedRuleSetIds = this.additionalInfo.selectedRuleSets.map(ruleSet => ruleSet._id);
    this.ruleSetsSelector.filterOptions = this.additionalInfo.companyRuleSets.map(ruleSet => {
      const isSelectedAndDisabled = selectedRuleSetIds.includes(ruleSet._id);
      return {
        label: ruleSet.name,
        value: ruleSet._id,
        selected: false,
        disabled: isSelectedAndDisabled,
      } as EnumListItem;
    });
  }

  /**
   * Update grid values.
   */
  private computeGridValues() {
    if (!this.additionalInfo) {
      return;
    }

    this.ruleSetsGrid.propertiesSet = false;
    (this.ruleSetsGrid.dataProvider as IntegrityMonitorPolicyRuleSetsGridDataProvider).ruleSets =
      this.additionalInfo.selectedRuleSets;
    this.ruleSetsGrid.propertiesSet = true;

    this.changeValidity(this.additionalInfo.selectedRuleSets.length > 0);
    this.toggleGridVisibility();
  }

  /**
   * Validate component.
   */
  public validate() {
    if (!this.shouldBeValidated) {
      this.changeValidity(true);
      return;
    }

    // value not loaded
    if (!this.value) {
      return;
    }

    this.changeValidity(this.value.length > 0);
  }

  /**
   * Change validity of the component.
   * @param isValid
   */
  private changeValidity(isValid: boolean) {
    this.validSubject.next(isValid);

    // when data is not loaded, we don't have access to the notice element
    if (!this.ruleSetsNotice) {
      return;
    }

    toggleVisibility(this.ruleSetsNotice, !isValid);
  }

  /**
   * Toggle grid visibility.
   */
  private toggleGridVisibility(): void {
    if (!this.ruleSetsGrid) {
      return;
    }

    toggleVisibility(
      this.ruleSetsGrid,
      !!(this.ruleSetsGrid.dataProvider as IntegrityMonitorPolicyRuleSetsGridDataProvider).ruleSets.length
    );
  }
}
